import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AppointmentAPI } from "../../services/appointment.service";
import type { AppointmentListItem, AppointmentStatus } from "../../services/appointment.service";
import { AppointmentStatusBadge } from "./AppointmentStatusBadge";
import { CancelAppointmentModal } from "./CancelAppointmentModal";
import { RejectAppointmentModal } from "./RejectAppointmentModal";
import { RescheduleAppointmentModal } from "./RescheduleAppointmentModal";
import { useRole } from "../../hooks/useRole";

interface Props {
  appointment: AppointmentListItem & {
    patient_name?: string;
    patient_display_id?: string;
    doctor_name?: string;
    slot_start?: string;
    slot_end?: string;
  };
  /** Current user role — controls which action buttons are visible */
  userRole?: "receptionist" | "doctor" | "admin";
  onRefresh: () => void;
  /** True when this card should be scrolled-to and visually highlighted (e.g. opened from a notification link) */
  highlighted?: boolean;
}

const fmt = (iso?: string) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Which transitions each role can trigger
const canApprove   = (role?: string) => role === "doctor"       || role === "admin";
const canReject    = (role?: string) => role === "doctor"       || role === "admin";
const canConfirm   = (role?: string) => role === "receptionist" || role === "admin" || role === "doctor";
const canCheckin   = (role?: string) => role === "receptionist" || role === "admin" || role === "doctor";
const canCancel    = (role?: string) => role === "receptionist" || role === "admin" || role === "doctor";
const canReschedule= (role?: string) => role === "receptionist" || role === "admin" || role === "doctor";
const canComplete  = (role?: string) => role === "doctor"       || role === "admin";
// Doctors/Admins can open the diagnosis pad for a checked-in patient
const canStartVisit= (role?: string) => role === "doctor"       || role === "admin";

// Valid next transitions per current status.
const TRANSITIONS: Record<AppointmentStatus, AppointmentStatus[]> = {
  requested:   ["approved", "cancelled"],
  approved:    ["scheduled", "cancelled"],
  scheduled:   ["confirmed", "cancelled"],
  confirmed:   ["in_progress", "cancelled"],
  in_progress: ["completed"],
  completed:   [],
  cancelled:   [],
  no_show:     [],
};

export const AppointmentCard: React.FC<Props> = ({ appointment, userRole, onRefresh, highlighted }) => {
  const { appointment_id, status, patient_name, patient_display_id, doctor_name, slot_start, slot_end, doctor_id, notes, arrived_at, patient_id, visit_id } = appointment;
  const navigate = useNavigate();
  const cardRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (highlighted) cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlighted]);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);

  const validNext = TRANSITIONS[status] ?? [];

  // The role helpers above say which transitions a role may ever make; the
  // grants say whether THIS user's role currently includes them. Every button
  // below changes an existing appointment, which the server gates on
  // appointments.manage — without it a View Appointments-only role saw
  // Confirm / Cancel / Reschedule and got a 403 on click. Starting a visit is a
  // clinical write (visits.write), not an appointment change.
  const { canOr } = useRole();
  const canManage = canOr("appointments.manage", true);
  const canWriteVisits = canOr("visits.write", true);
  const allow = (roleAllows: boolean) => canManage && roleAllows;

  // A patient is "checked in" once they've arrived and the appointment moved to
  // in_progress. From there a doctor/admin can open the diagnosis pad directly.
  // Resume the existing visit if one is already linked so we don't create a duplicate.
  const isCheckedIn = status === "in_progress";
  // A walk-in has no booked slot to move - it's managed from the live queue instead.
  const isWalkin = appointment.source === "walk_in";
  const startVisit = () => {
    const params = new URLSearchParams({ patient_id, doctor_id: doctor_id ?? "" });
    if (visit_id) params.set("visit_id", visit_id);
    navigate(`/90sdiagnosis?${params.toString()}`);
  };

  const doAction = async (action: string, fn: () => Promise<unknown>) => {
    setLoadingAction(action);
    try {
      await fn();
      toast.success(`Appointment ${action}`);
      onRefresh();
    } catch (e: any) {
      toast.error(e.message || `Failed to ${action} appointment`);
    } finally {
      setLoadingAction(null);
    }
  };

  const isTerminal = status === "completed" || status === "cancelled" || status === "no_show";

  return (
    <>
      <div
        ref={cardRef}
        id={`appointment-${appointment_id}`}
        className={`bg-white rounded-2xl border shadow-sm px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between hover:shadow-md transition-shadow ${
          highlighted ? "border-cyan-400 ring-2 ring-cyan-200" : "border-slate-100"
        }`}
      >
        {/* Left: time + names */}
        <div className="flex items-start gap-4 min-w-0">
          {/* Time block */}
          <div className="flex-shrink-0 w-20 text-center">
            <div className="text-base font-bold text-slate-800">{fmt(slot_start)}</div>
            <div className="text-xs text-slate-400">{fmt(slot_end)}</div>
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-800 truncate">
                {patient_name ?? "Unknown Patient"}
              </span>
              {(patient_display_id || !patient_name) && (
                <span className="text-[11px] text-slate-400 font-mono flex-shrink-0">
                  {patient_display_id ?? appointment.patient_id.slice(0, 8).toUpperCase()}
                </span>
              )}
              <AppointmentStatusBadge status={status} size="sm" />
            </div>
            <div className="text-xs text-slate-400 mt-0.5 truncate">
              {doctor_name ?? "—"}
              {appointment.booking_source === "public" && (
                <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 text-[10px] font-medium border border-purple-200">
                  Online Booking
                </span>
              )}
              {notes && <span className="ml-2 italic">"{notes}"</span>}
            </div>
            {status === "requested" && appointment.booking_source === "public" && (
              <div className="text-xs text-yellow-600 mt-0.5 font-medium">
                Waiting for your approval
              </div>
            )}
            {status === "approved" && (
              <div className="text-xs text-orange-600 mt-0.5 font-medium">
                Approved — awaiting patient payment
              </div>
            )}
            {appointment.rejection_reason && (
              <div className="text-xs text-red-500 mt-0.5">
                Rejected: {appointment.rejection_reason}
              </div>
            )}
            {arrived_at && (
              <div className="text-xs text-amber-600 mt-0.5">
                Arrived {fmt(arrived_at)}
              </div>
            )}
          </div>
        </div>

        {/* Right: action buttons + view link */}
        <div className="flex flex-wrap gap-2 flex-shrink-0 items-center">
          {isCheckedIn && canStartVisit(userRole) && canWriteVisits && (
            <button
              onClick={startVisit}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition shadow-sm"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Start Visit
            </button>
          )}
          <Link
            to={`/appointments/confirmation/${appointment_id}`}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-500 hover:bg-slate-200 transition"
          >
            View
          </Link>
          {!isTerminal && (
            <>
              {validNext.includes("approved") && allow(canApprove(userRole)) && (
                <button
                  onClick={() => doAction("approved", () => AppointmentAPI.approve(appointment_id))}
                  disabled={!!loadingAction}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-500 text-white hover:bg-green-600 disabled:opacity-40 transition"
                >
                  {loadingAction === "approved" ? "…" : "Approve"}
                </button>
              )}
              {status === "approved" && allow(canApprove(userRole)) && (
                <button
                  onClick={() => doAction("resent", () => AppointmentAPI.approve(appointment_id))}
                  disabled={!!loadingAction}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-40 transition"
                >
                  {loadingAction === "resent" ? "…" : "Resend Payment Link"}
                </button>
              )}
              {status === "requested" && allow(canReject(userRole)) && (
                <button
                  onClick={() => setShowReject(true)}
                  disabled={!!loadingAction}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 transition"
                >
                  Reject
                </button>
              )}
              {validNext.includes("confirmed") && allow(canConfirm(userRole)) && (
                <button
                  onClick={() => doAction("confirmed", () => AppointmentAPI.confirm(appointment_id))}
                  disabled={!!loadingAction}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-40 transition"
                >
                  {loadingAction === "confirmed" ? "…" : "Confirm"}
                </button>
              )}
              {validNext.includes("in_progress") && allow(canCheckin(userRole)) && (
                <button
                  onClick={() => doAction("checked in", () => AppointmentAPI.checkin(appointment_id))}
                  disabled={!!loadingAction}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 transition"
                >
                  {loadingAction === "checked in" ? "…" : "Check In"}
                </button>
              )}
              {validNext.includes("completed") && allow(canComplete(userRole)) && (
                <button
                  onClick={() => doAction("completed", () => AppointmentAPI.complete(appointment_id))}
                  disabled={!!loadingAction}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-500 text-white hover:bg-green-600 disabled:opacity-40 transition"
                >
                  {loadingAction === "completed" ? "…" : "Complete"}
                </button>
              )}
              {status !== "in_progress" && allow(canReschedule(userRole)) && doctor_id && !isWalkin && (
                <button
                  onClick={() => setShowReschedule(true)}
                  disabled={!!loadingAction}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 transition"
                >
                  Reschedule
                </button>
              )}
              {validNext.includes("cancelled") && allow(canCancel(userRole)) && (
                <button
                  onClick={() => setShowCancel(true)}
                  disabled={!!loadingAction}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-red-500 hover:bg-red-50 disabled:opacity-40 transition"
                >
                  Cancel
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <RejectAppointmentModal
        isOpen={showReject}
        appointmentId={appointment_id}
        onClose={() => setShowReject(false)}
        onSuccess={() => { setShowReject(false); onRefresh(); }}
      />

      <CancelAppointmentModal
        isOpen={showCancel}
        appointmentId={appointment_id}
        onClose={() => setShowCancel(false)}
        onSuccess={() => { setShowCancel(false); onRefresh(); }}
      />

      <RescheduleAppointmentModal
        isOpen={showReschedule}
        appointmentId={appointment_id}
        doctorId={doctor_id ?? ""}
        currentSlotId={appointment.slot_id ?? undefined}
        currentSlotStart={slot_start}
        onClose={() => setShowReschedule(false)}
        onSuccess={() => { setShowReschedule(false); onRefresh(); }}
      />
    </>
  );
};